import {
  BasketballEventReplyAnswer,
  BasketballEventUserRolePlayerCoach,
  BasketballEventUserRoleType,
  BasketballPractice,
  BasketballPracticeComment,
  BasketballPracticeCommentPagination,
  BasketballPracticeReply,
  BasketballTeamPlayerReply,
  BasketballTeamTask,
  RequestBasePostTaskAssignments,
  ResponseBasePostTaskAssignments,
  ResponseBasketballUpdatePracticeNominees,
} from '@hefe/models';
import {
  PayloadAction,
  createAsyncThunk,
  createSelector,
  createSlice,
  isAnyOf,
} from '@reduxjs/toolkit';
import API from '../../../api/api';
import {
  BasketballPracticeAddCommentRequest,
  BasketballPracticeSaveRequest,
  BasketballPracticesLinkRequest,
  BasketballPracticesSaveRequest,
  BasketballPracticesUpdateRequest,
} from '../../../models/requests/basketball-practice';
import { tasksApi } from '../../base/tasks/tasks.api';
import {
  setBasketballPracticeHelpouts,
  setBasketballPracticeNominees,
} from '../actions/basketball-practices/basketball-practices.actions';
import { assignUsersToBasketballEventTask } from '../basketball-event-tasks/basketball-event-tasks.slice';
import { updateUserBasketballPracticeReply } from '../basketball-events/basketball-events.slice';
import { basketballTeamsSlice } from '../basketball-teams/basketball-teams.slice';

const FEATURE_KEY = 'basketballPractices';

/************************************************************************************
 *
 *                                  ACTIONS
 *
 ************************************************************************************
 */

export const fetchBasketballPractice = createAsyncThunk(
  'basketballPractices/getSingle',
  async (id: number, thunkAPI) => {
    const response = await API.basketballPractices().getSingle(id);
    return response?.data;
  }
);

export const saveBasketballPractice = createAsyncThunk(
  'basketballPractices/save',
  async (data: BasketballPracticeSaveRequest, thunkAPI) => {
    const response = await API.basketballPractices().savePractice(data);
    return response?.data;
  }
);

export const saveBasketballPractices = createAsyncThunk(
  'basketballPractices/saveMulti',
  async (data: BasketballPracticesSaveRequest, thunkAPI) => {
    const response = await API.basketballPractices().createPractices(data);
    return response?.data;
  }
);

export const linkBasketballPractices = createAsyncThunk(
  'basketballPractices/link',
  async (
    data: {
      teamId: number;
      request: BasketballPracticesLinkRequest;
    },
    thunkAPI
  ) => {
    const response = await API.basketballPractices().linkPractices(
      data.request
    );

    thunkAPI.dispatch(
      basketballTeamsSlice.actions.setTeamSyncUntil({
        teamId: data.teamId,
        syncUntil: data.request.date_till,
      })
    );

    return response?.data;
  }
);

export const updateBasketballPractices = createAsyncThunk(
  'basketballPractices/updateMulti',
  async (data: BasketballPracticesUpdateRequest, thunkAPI) => {
    const response = await API.basketballPractices().updatePractices(data);
    return response?.data;
  }
);

export const syncBasketballPractices = createAsyncThunk(
  'basketballPractices/sync',
  async (thunkAPI) => {
    const response = await API.basketballPractices().syncPractices();
    return response?.data;
  }
);

export const deleteBasketballPractices = createAsyncThunk(
  'basketballPractices/deleteMulti',
  async (data: number[], thunkAPI) => {
    const response = await API.basketballPractices().deletePractices({
      ids: data,
    });
    return response?.data;
  }
);

export const addCommentToPractice = createAsyncThunk(
  'basketballPractices/addComment',
  async (
    data: { practiceId: number; payload: BasketballPracticeAddCommentRequest },
    thunkAPI
  ) => {
    const response = await API.basketballPracticeComments(
      data.practiceId
    ).addComment(data.payload);
    return response?.data;
  }
);

export const fetchBasketballPracticeComments = createAsyncThunk(
  'basketballPractices/getComments',
  async (data: { practiceId: number; page: number }, thunkAPI) => {
    const response = await API.basketballPracticeComments(
      data.practiceId
    ).getComments(data.page);
    return {
      data: response?.data,
      practiceId: data.practiceId,
    };
  }
);

/************************************************************************************
 *
 *                                      SLICE
 *
 ************************************************************************************
 */

export interface BasketballPracticesState {
  loadingPracticeDetails: boolean;
  practiceDetails: { [key: number]: BasketballPractice };
}

const initialState: BasketballPracticesState = {
  practiceDetails: {},
  loadingPracticeDetails: false,
};

export const basketballPracticesSlice = createSlice({
  name: FEATURE_KEY,
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    /******************************************************************************************
     *
     *                                  LOAD PRACTICE DETAILS
     *
     ******************************************************************************************
     */
    builder.addCase(
      fetchBasketballPractice.pending,
      (state: BasketballPracticesState) => {
        state.loadingPracticeDetails = true;
      }
    );
    builder.addCase(
      fetchBasketballPractice.fulfilled,
      (
        state: BasketballPracticesState,
        action: PayloadAction<BasketballPractice>
      ) => {
        state.practiceDetails[action.payload.id] = action.payload;
        state.loadingPracticeDetails = false;
      }
    );
    builder.addCase(
      fetchBasketballPractice.rejected,
      (state: BasketballPracticesState) => {
        state.loadingPracticeDetails = false;
      }
    );
    /******************************************************************************************
     *
     *                                     Others
     *
     ******************************************************************************************
     */
    builder.addCase(
      addCommentToPractice.fulfilled,
      (
        state: BasketballPracticesState,
        action: PayloadAction<BasketballPracticeComment>
      ) => {
        const comment = action.payload;
        state.practiceDetails[comment.practice_id].comments.data.unshift(
          comment
        );
      }
    );
    builder.addCase(
      fetchBasketballPracticeComments.fulfilled,
      (
        state: BasketballPracticesState,
        action: PayloadAction<{
          practiceId: number;
          data: BasketballPracticeCommentPagination;
        }>
      ) => {
        const { data, practiceId } = action.payload;
        const comments = state.practiceDetails[practiceId].comments;

        state.practiceDetails[practiceId].comments = {
          ...data,
          data: [...(comments?.data || []), ...data.data],
        };
      }
    );

    /******************************************************************************************
     *
     *                                  Team Details
     *
     ******************************************************************************************
     */
    builder.addCase(
      assignUsersToBasketballEventTask.fulfilled,
      (
        state: BasketballPracticesState,
        action: PayloadAction<BasketballTeamTask>
      ) => {
        const task = action.payload;
        const practiceId = task.updated_practice_id;
        const teamId = task.team_id;
        if (!practiceId) return;

        const tIndex = teamIndex(state.practiceDetails, practiceId, teamId);
        if (tIndex < 0) return;
        const taskIndex = state.practiceDetails[practiceId]!.teams![
          tIndex
        ].practice_tasks!.findIndex(
          (t: BasketballTeamTask) => t.id === task.id
        );
        if (taskIndex == null) return;
        state.practiceDetails[practiceId]!.teams![tIndex].practice_tasks![
          taskIndex
        ] = task;
      }
    );
    builder.addCase(
      updateUserBasketballPracticeReply.fulfilled,
      (
        state: BasketballPracticesState,
        action: PayloadAction<BasketballPracticeReply>
      ) => {
        const practiceId = action.payload.practice_id;
        const oldReply = action.payload.old_reply;
        const newReply = action.payload.reply;
        const userId = action.payload.repliable_id;

        const oldReplyType =
          oldReply != null
            ? oldReply === BasketballEventReplyAnswer.COMMITMENT
              ? 'yes'
              : 'no'
            : 'unknown';
        const newReplyType =
          newReply != null
            ? newReply === BasketballEventReplyAnswer.COMMITMENT
              ? 'yes'
              : 'no'
            : 'unknown';

        if (!state.practiceDetails[practiceId]) {
          return;
        }

        for (const team of state.practiceDetails[practiceId].teams) {
          if (!team.playerReplies) {
            continue;
          }
          const user = team.playerReplies[oldReplyType].find(
            (u: BasketballTeamPlayerReply) => u.id === userId
          );
          // Remove old player
          team.playerReplies[oldReplyType] = team.playerReplies[
            oldReplyType
          ].filter((u: BasketballTeamPlayerReply) => u.id !== userId);

          // Add player
          if (user) {
            user.reply = action.payload;
            team.playerReplies[newReplyType].push(user);
          }
        }

        // update user role answer
        const practice = state.practiceDetails[practiceId];
        const userRoleUserIndex = practice.user_data?.findIndex(
          (u) => u.user.id === userId
        );
        const userRoleRoleIndex = practice?.user_data[
          userRoleUserIndex
        ]?.roles?.findIndex(
          (r) => r.type === BasketballEventUserRoleType.PLAYER
        );
        const userRole = practice?.user_data[userRoleUserIndex]?.roles[
          userRoleRoleIndex
        ] as BasketballEventUserRolePlayerCoach;

        if (userRole && userRole.reply) {
          userRole.reply.reply = newReply;
        }
      }
    );
    builder.addCase(
      setBasketballPracticeNominees.fulfilled,
      (
        state: BasketballPracticesState,
        action: PayloadAction<ResponseBasketballUpdatePracticeNominees>
      ) => {
        // Update game details
        const practiceDetails =
          state.practiceDetails[action.payload.practice_id];
        if (practiceDetails && practiceDetails.teams) {
          for (const team of practiceDetails.teams) {
            if (team.id === action.payload.team_id) {
              team.playerReplies = action.payload.player_replies;
              team.nominees = action.payload.nominees;
            }
          }
        }
      }
    );
    builder.addCase(
      setBasketballPracticeHelpouts.fulfilled,
      (
        state: BasketballPracticesState,
        action: PayloadAction<BasketballPractice>
      ) => {
        // Update practice details
        const practiceDetails = state.practiceDetails[action.payload.id];
        if (practiceDetails) {
          state.practiceDetails[action.payload.id] = action.payload;
        }
      }
    );
    builder.addMatcher(
      isAnyOf(tasksApi.endpoints.assignToTask.matchFulfilled),
      (
        state: BasketballPracticesState,
        action: PayloadAction<ResponseBasePostTaskAssignments>
      ) => {
        const meta = (action as any).meta;
        const originalArgs = meta.arg.originalArgs;
        const taskId = originalArgs.taskId;
        const requestData =
          originalArgs.request as RequestBasePostTaskAssignments;

        const practiceDetails =
          state.practiceDetails[requestData?.practice_id ?? 0];
        if (practiceDetails && practiceDetails.teams) {
          const team = practiceDetails.teams.find(
            (t) => t.id === requestData.basketball_team_id
          );
          if (team) {
            team.tasks = team.tasks?.map((task) => {
              if (task.id === taskId) {
                return {
                  ...task,
                  assignments: action.payload.assignments,
                };
              }
              return task;
            });
          }

          // Club Details
          if (practiceDetails && practiceDetails.club_tasks) {
            practiceDetails.club_tasks = practiceDetails.club_tasks?.map(
              (task) => {
                if (task.id === taskId) {
                  return {
                    ...task,
                    assignments: action.payload.assignments,
                  };
                }
                return task;
              }
            );
          }
        }
      }
    );
  },
});

/*
 * Export reducer for store configuration.
 */
export const basketballPracticesReducer = basketballPracticesSlice.reducer;
export const basketballPracticesActions = basketballPracticesSlice.actions;

const getBasketballPracticesState = (
  rootState: any
): BasketballPracticesState => rootState[FEATURE_KEY];

export const basketballPracticesState = createSelector(
  getBasketballPracticesState,
  (state) => state
);

/******************************************************************************************
 *
 *                                  HELPER FUNCTIONS
 *
 ******************************************************************************************
 */
const teamIndex = (
  practices: { [key: string]: BasketballPractice },
  practiceId: number,
  teamId: number
): number => {
  let returnValue = -1;
  const practice = practices[practiceId];
  if (practice) {
    const index = practices[practiceId].teams?.findIndex(
      (team) => team.id === teamId
    );
    returnValue = index != null ? index : -1;
  }

  return returnValue;
};
